import { useMemo } from 'react';
import type { ExternalError, ExternalMessage } from '@atlaskit/jql-editor';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useClausesForField } from '@atlassian/jira-jql-utils/src/utils/use-clauses-for-field/index.tsx';
import { useAST } from '../index.tsx';
import { ASSIGNEE } from '../../../common/constants.tsx';
import { getRootLevelClause } from '../get-root-level-clause/index.tsx';

/**
 * Determines if the assignee picker should be enabled depending on JQL/AST complexity
 * The assignee picker should be enabled if:
 * - No JQL errors are present
 * - Maximum one assignee clause is present
 * - if 1 assignee clause is present:
 *   - it's at the root level
 *   - it's connected by AND operator
 *   - it has operators = or IN
 * - if no assignee clause is present, picker is enabled regardless of operator
 */
export const useIsAssigneePickerEnabled = (jqlMessages: ExternalMessage[] | undefined) => {
	const ast = useAST();
	const assigneeClauses = useClausesForField(ast, ASSIGNEE);
	const { isValid, clause: rootLevelClause } = useMemo(
		() => getRootLevelClause(ast, ASSIGNEE),
		[ast],
	);

	const jqlErrors = useMemo(
		() => (jqlMessages || []).filter((e): e is ExternalError => e.type === 'error'),
		[jqlMessages],
	);

	if (jqlErrors.length > 0) {
		return false;
	}

	// If there are more than one assignee clauses, disable picker
	if (assigneeClauses.length > 1) {
		return false;
	}

	// If there's an assignee clause but it's not at root level, disable picker
	if (assigneeClauses.length === 1 && !rootLevelClause) {
		return false;
	}

	// If there's an assignee clause at root level but it's not valid (wrong operator), disable picker
	if (!isValid) {
		return false;
	}

	// Otherwise, enable picker
	return true;
};

export const useIsAssigneePickerEnabledWithFG = functionWithCondition(
	() => fg('list_lovability_improving_filters'),
	useIsAssigneePickerEnabled,
	() => true,
);
